// GLobal
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Field,
  GetStaticComponentProps,
  SitecoreContextValue,
} from '@sitecore-jss/sitecore-jss-nextjs';

// Lib
import clientFactory from 'lib/graphql-client-factory';
import { ComponentProps } from 'lib/component-props';
import { BoKNavigation } from 'lib/templates/Feature.BOKF.model';
import { useNavContext } from 'lib/react-context/NavContext';
import { useRealPathName } from 'lib/utils/useRealPathname';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { hostToReplace } from 'lib/host-to-replace';

// Local
import { SubItemType, LeprechaunHeaderProps, ItemType } from '../Header/Header';
import SVG from 'helpers/SVG/SVG';
import LeftNavigationQuery from './LeftNavigation.graphql';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';

type LeftNavigationProps = ComponentProps &
  BoKNavigation.NavigationConfiguration & {
    staticProps?: {
      header?: LeprechaunHeaderProps; // Left Navigation component uses the same data as header
    };
  };

type ExtendedSitecoreContextProps = SitecoreContextValue & {
  IncludeFDICLogo?: Field<boolean>;
  DiscludeFDIC?: Field<boolean>;
};

const LeftNavigation = (serverProps: LeftNavigationProps): JSX.Element => {
  const props = { fields: serverProps?.staticProps?.header };

  const extendedSitecoreContext: ExtendedSitecoreContextProps =
    useSitecoreContext().sitecoreContext;

  const includeFDICLogo = extendedSitecoreContext.IncludeFDICLogo?.value;
  const discludeFDIC = extendedSitecoreContext.route?.fields?.['DiscludeFDIC'] as Field<boolean>;
  const isFDICExists = includeFDICLogo && !discludeFDIC?.value;

  function filterFunc(value: ItemType) {
    return value?.template?.name === 'NavigationLink';
  }

  function filterFunc2(value: ItemType) {
    return value?.template?.name === 'Promo Button';
  }

  // look up Promo Button for subscribe button
  const primaryNavData = serverProps?.staticProps?.header?.children?.results.filter(filterFunc);
  const subscribeButton = serverProps?.staticProps?.header?.children?.results.filter(filterFunc2);

  const pathname = useRealPathName();
  const strippedPath = pathname?.split('/', 2) || ''; // get the first item in path

  const isItem = (item: SubItemType) => {
    const displayName = item?.displayName
      ?.toLowerCase()
      .replaceAll(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '')
      .replaceAll(' ', '');
    const stripped = strippedPath[1]
      .toLowerCase()
      .replaceAll(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '')
      .replaceAll(' ', '');
    return displayName === stripped;
  };

  let selectedItem =
    primaryNavData?.find(isItem) ||
    (primaryNavData && primaryNavData.length === 1 && primaryNavData[0]);

  const isHomepageNavEnabled = serverProps?.fields?.EnableHomePageLeftNavigation?.value;
  // Homepage lhn check
  if (selectedItem === false && pathname === '/' && isHomepageNavEnabled) {
    selectedItem = (primaryNavData ?? [])[0];
  }

  const [navOpen, setNavOpen] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [prevScrollPosition, setPrevScrollPosition] = useState(0);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [scrollPositionZero, setScrollPositionZero] = useState(false);
  const [alertNoticeHeight, setAlertNoticeHeight] = useState(0);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [hasAlerts, setHasAlerts] = useState(false);

  const sitecoreContext = useSitecoreContext();
  const navContext = useNavContext();

  function toggleDrawer() {
    setNavOpen(!navOpen);
    navContext.setLeftNavState(navOpen ? 'collapsed' : 'expanded');
  }

  const updateAlertBannerHeight = () => {
    // Get all alert containers that aren't hidden
    const alertContainers = document.querySelectorAll('.alert-container:not(.hidden)');
    if (alertContainers.length === 0) {
      setHasAlerts(false);
      document.documentElement.style.removeProperty('--alert-notice-height');
    } else {
      const headerElement = document.querySelector('header');
      let totalHeight = 0;

      // Only process alerts that are above the header
      alertContainers.forEach((container) => {
        if (headerElement) {
          const alertRect = container.getBoundingClientRect();
          const headerRect = headerElement.getBoundingClientRect();

          // Check if alert is above header
          if (alertRect.top < headerRect.top) {
            totalHeight += container.clientHeight;
          }
        }
      });

      // Update the CSS variable with total height
      setHasAlerts(true);
      document.documentElement.style.setProperty('--alert-notice-height', `${totalHeight}px`);
    }
  };

  let leftNavExists;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    leftNavExists = document.getElementById('sidebar-navigation')?.hasChildNodes();
    navContext.setNavItem((selectedItem as ItemType)?.displayName);
    updateAlertBannerHeight();
    window.addEventListener('resize', updateAlertBannerHeight);

    // Cleanup function
    return () => {
      window.removeEventListener('resize', updateAlertBannerHeight);
    };
  }, []);

  const router = useRouter();
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRouteChange = () => {
      const { CollapseSidebar } = sitecoreContext?.sitecoreContext?.route?.fields || {};
      const isSideBarCollapsed = (CollapseSidebar as Field<boolean>)?.value || false;
      setNavOpen(!isSideBarCollapsed);
      navContext.setLeftNavState(isSideBarCollapsed ? 'collapsed' : 'expanded');
    };

    // Initial call
    handleRouteChange();

    // Subscribe to route changes
    router.events.on('routeChangeComplete', handleRouteChange);

    // Cleanup subscription on unmount
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  const handleScroll = () => {
    const position = window.scrollY;
    setScrollPositionZero(position === 0);
    setIsScrollingUp(position < prevScrollPosition);
    setPrevScrollPosition(position);
    setScrollPosition(position);
    const isHidden = document.querySelector('.header-hidden') !== null;
    setIsHeaderHidden(isHidden);
  };

  useEffect(() => {
    if (hasAlerts) {
      updateAlertBannerHeight();
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [prevScrollPosition]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const alertHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--alert-notice-height')
      );
      setAlertNoticeHeight(alertHeight);

      // Add listener for alert height changes
      const handleAlertHeightChange = (event: Event) => {
        const customEvent = event as CustomEvent;
        if (customEvent.detail && customEvent.detail.alertHeight !== undefined) {
          setAlertNoticeHeight(customEvent.detail.alertHeight);
          setHasAlerts(customEvent.detail.alertHeight > 0);
        }
      };

      document.addEventListener('alertheightchanged', handleAlertHeightChange);

      // Clean up event listener on unmount
      return () => {
        document.removeEventListener('alertheightchanged', handleAlertHeightChange);
      };
    }
    return undefined; // Explicitly return undefined for non-cleanup paths
  }, []);

  const isScrolledPastAlert = scrollPosition > alertNoticeHeight;
  // 100 comes from fixed height of fdic banner + the height of the header
  const alertNoticeTopValue =
    scrollPosition > alertNoticeHeight ? 0 : alertNoticeHeight - scrollPosition + 100;

  const mainPositioningClasses = isFDICExists
    ? hasAlerts
      ? scrollPositionZero
        ? 'top-[calc(var(--alert-notice-height)+100px)] h-[calc(100vh-var(--alert-notice-height)-100px)]'
        : !scrollPositionZero && !isScrolledPastAlert
        ? ''
        : !scrollPositionZero && isScrolledPastAlert && !isHeaderHidden
        ? 'top-25 h-[calc(100vh-100px)]'
        : 'top-0 h-screen'
      : scrollPositionZero
      ? 'top-25 h-[calc(100vh-100px)]'
      : !scrollPositionZero && !isHeaderHidden
      ? 'top-25 h-[calc(100vh-100px)]'
      : 'top-0 h-screen'
    : scrollPositionZero
    ? 'top-14 h-[calc(100vh-56px)]'
    : !scrollPositionZero && !isHeaderHidden
    ? 'top-14 h-[calc(100vh-56px)]'
    : 'top-0 h-screen';

  const logoPositioningClasses = isFDICExists
    ? hasAlerts
      ? scrollPositionZero
        ? 'hidden'
        : !scrollPositionZero && !isScrolledPastAlert
        ? 'hidden'
        : !scrollPositionZero && isScrolledPastAlert && !isHeaderHidden
        ? 'hidden'
        : ''
      : scrollPositionZero
      ? 'hidden'
      : !scrollPositionZero && !isHeaderHidden
      ? 'hidden'
      : ''
    : scrollPositionZero
    ? 'hidden'
    : !scrollPositionZero && !isHeaderHidden
    ? 'hidden'
    : '';

  if (
    selectedItem &&
    selectedItem?.children?.results &&
    selectedItem?.children?.results?.length > 0
  ) {
    return (
      <aside
        id="sidebar-navigation"
        style={
          isFDICExists && hasAlerts && !scrollPositionZero && !isScrolledPastAlert
            ? {
                top: `${alertNoticeTopValue}px`,
                height: `calc(100vh - ${alertNoticeTopValue}px)`,
              }
            : {}
        }
        className={`left-nav hidden lg:flex flex-col pb-8 sticky z-10 bg-light-background-neutral-variant shadow-small ${mainPositioningClasses} ${
          navContext.leftNavState === 'expanded' ? '' : ''
        } ${
          !navContext.navItem
            ? '' // if we are not on a primary nav page, break out of this nested ternary early and leave no space for left nav
            : navContext.leftNavState === 'expanded'
            ? 'min-w-80 max-w-80'
            : `${leftNavExists ? '' : 'max-w-12'}`
        }`}
      >
        <LinkWrapper
          id="underlogo"
          field={props.fields?.logoLink?.jsonValue || {}}
          className={`h-nav-bar flex px-8 py-2 ml-2 min-h-14 ${logoPositioningClasses}`}
        >
          <div className={`h-full ${navOpen ? '' : 'hidden'}`}>
            <ImageWrapper
              field={props.fields?.logo?.jsonValue}
              editable={true}
              className={`w-full h-full ${
                !isScrollingUp && scrollPosition > alertNoticeHeight + 100 ? 'show' : ''
              }`}
              height="56"
              width="256"
            />
          </div>
        </LinkWrapper>
        <button
          tabIndex={0}
          className="btn-lhn-expand-collapse"
          onClick={toggleDrawer}
          aria-label="Left Side Navigation Toggle Button"
        >
          {navOpen ? (
            <SVG
              className="flex items-center rotate-180 [&_svg]:h-6 [&_svg]:w-6"
              svg="icon-chevron-right"
            ></SVG>
          ) : (
            <SVG
              className="flex items-center [&_svg]:h-6 [&_svg]:w-6"
              svg="icon-chevron-right"
            ></SVG>
          )}
          <SVG className="ml-1" svg="icon-menu"></SVG>
        </button>

        <nav
          className={`flex flex-col h-[calc(100vh-var(--nav-height))] pl-8 pr-2 mr-8 mt-5 ${
            navOpen ? 'overflow-y-auto bok-scrollbar bok-scrollbar--no-border' : 'overflow-y-hidden'
          }`}
        >
          <ul className={`mt-1 ${navOpen ? '' : 'hidden'}`}>
            {selectedItem.children?.results && selectedItem.children.results.length > 0
              ? selectedItem.children.results.map((item, key) =>
                  item && Object.keys(item).length > 0 ? (
                    <React.Fragment key={key}>
                      {/* Static label */}
                      {item.labelText?.jsonValue?.value && (
                        <li
                          key={key}
                          className={`c1 text-light-fill-primary-variant pl-2 ${
                            key > 0 ? 'mt-6 pt-6 border-t stroke-light-stroke-primary' : ''
                          }`}
                        >
                          {item.labelText?.jsonValue?.value}
                        </li>
                      )}
                      {/* END Static Label */}
                      {/* Secondary Links */}
                      {item.link?.jsonValue?.value && (
                        <li>
                          <LinkWrapper
                            className={`${
                              pathname &&
                              pathname ===
                                hostToReplace(item.link?.jsonValue)?.value?.href?.toLowerCase()
                                ? 'no-underline text-light-fill-primary bg-light-background-neutral-high '
                                : ''
                            } flex p-2 p2-medium leading-normal rounded hover:no-underline hover:text-light-fill-primary hover:bg-light-background-neutral-high focus:no-underline focus:outline-light-stroke-primary-variant focus:bg-light-background-neutral-high focus:outline focus:outline-2 focus-visible:outline-light-stroke-primary-variant focus-visible:outline focus-visible:outline-2 active:bg-light-background-neutral-high active:outline-light-stroke-primary-variant active:outline-2 active:outline`}
                            field={item.link?.jsonValue || {}}
                          >
                            {item.text?.jsonValue?.value}
                          </LinkWrapper>
                          {/* Tertiary Links */}
                          {item.children?.results && item.children.results.length > 0 && (
                            <ul>
                              {item.children?.results.map((subItem, key: number) => {
                                return (
                                  <li key={key}>
                                    <LinkWrapper
                                      className={`${
                                        pathname &&
                                        pathname ===
                                          hostToReplace(
                                            subItem.link?.jsonValue
                                          )?.value?.href?.toLowerCase()
                                          ? 'p2-medium leading-normal no-underline bg-light-background-neutral-high'
                                          : 'p2 leading-normal'
                                      } flex py-2 pr-2 pl-4 text-light-fill-primary rounded hover:no-underline hover:text-light-fill-primary hover:bg-light-background-neutral-high focus:no-underline focus:outline-light-stroke-primary-variant focus:bg-light-background-neutral-high focus:outline focus:outline-2 focus-visible:outline-light-stroke-primary-variant focus-visible:outline focus-visible:outline-2 active:bg-light-background-neutral-high active:outline-light-stroke-primary-variant active:outline-2 active:outline`}
                                      field={subItem.link?.jsonValue || {}}
                                    >
                                      {subItem.text?.jsonValue?.value}
                                    </LinkWrapper>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          {/* END Tertiary Links */}
                        </li>
                      )}
                      {/* END Secondary Label */}
                    </React.Fragment>
                  ) : null
                )
              : null}
          </ul>
          <ul className={`mt-4 space-y-2.5 ${navOpen ? '' : 'hidden'}`}>
            {subscribeButton?.[0]?.children?.results?.map((item, key) => {
              if (!item?.link?.jsonValue) return null;
              return (
                <li className="" key={key}>
                  <LinkWrapper
                    className="btn btn-auxiliary w-full hover:no-underline focus:no-underline"
                    field={{
                      value: {
                        href: item.link?.jsonValue?.value?.href,
                        text: item.text?.jsonValue?.value,
                        class: item.link?.jsonValue?.value?.class,
                      },
                    }}
                  />
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    );
  } else {
    return <></>;
  }
};

export default LeftNavigation;

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData) => {
  const graphQLClient = clientFactory({});
  const result = await graphQLClient.request<unknown>(LeftNavigationQuery, {
    datasource: rendering.dataSource,
    language: layoutData?.sitecore?.context?.language,
  });
  return {
    staticProps: result,
  };
};
